import React, {useState} from "react";
import {Form, Table} from "react-bootstrap";
import ModalDialog from "common/components/Modal";
import {buildFlagUrl, isValidAlpha2TeamCode, normalizeTeamCode} from "./constants";

export default function TeamManagement({api, eventId, teams, onRefresh, notify}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [teamCode, setTeamCode] = useState("");
  const [teamName, setTeamName] = useState("");
  const [flagUrl, setFlagUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [useYn, setUseYn] = useState("Y");

  if (!eventId) {
    return <p>이벤트를 먼저 선택해주세요.</p>;
  }

  function resetForm() {
    setEditing(null);
    setTeamCode("");
    setTeamName("");
    setFlagUrl("");
    setSortOrder(0);
    setUseYn("Y");
  }

  function startEdit(team) {
    setEditing(team);
    setTeamCode(team.teamCode ?? "");
    setTeamName(team.teamName ?? "");
    setFlagUrl(team.flagUrl ?? "");
    setSortOrder(team.sortOrder ?? 0);
    setUseYn(team.useYn ?? "Y");
  }

  function onTeamCodeChange(value) {
    const code = normalizeTeamCode(value).slice(0, 2);
    setTeamCode(code);
    setFlagUrl(buildFlagUrl(code));
  }

  function applyAutoFlagUrl() {
    const auto = buildFlagUrl(teamCode);
    if (!auto) {
      alert("유효한 팀 코드(2글자)를 먼저 입력해주세요.");
      return;
    }
    setFlagUrl(auto);
  }

  function validate() {
    const code = normalizeTeamCode(teamCode);
    if (!isValidAlpha2TeamCode(code)) {
      alert("팀 코드는 ISO alpha-2 형식(2글자)이어야 합니다. 예: BR, MX, JP");
      return false;
    }
    if (!teamName.trim()) {
      alert("팀명을 입력해주세요.");
      return false;
    }
    const resolvedFlagUrl = flagUrl.trim() || buildFlagUrl(code);
    if (!resolvedFlagUrl) {
      alert("국기 URL을 입력하거나 팀 코드로 자동 생성해주세요.");
      return false;
    }
    return true;
  }

  function onSave() {
    if (!validate()) {
      setConfirmOpen(false);
      return;
    }
    const code = normalizeTeamCode(teamCode);
    const resolvedFlagUrl = flagUrl.trim() || buildFlagUrl(code);
    const payload = {
      eventId,
      teamCode: code,
      teamName: teamName.trim(),
      flagUrl: resolvedFlagUrl,
      sortOrder: Number(sortOrder) || 0,
      useYn,
    };
    const request = editing
      ? api.updateWorldcupTeam({...payload, teamId: editing.teamId})
      : api.createWorldcupTeam(payload);

    request.then((res) => {
      if (res?.result === "ok") {
        notify(editing ? "팀 정보가 수정되었습니다." : "팀이 등록되었습니다.");
        resetForm();
        onRefresh();
      } else {
        notify(res?.message ?? "실패하였습니다.", false);
      }
    }).finally(() => setConfirmOpen(false));
  }

  const previewUrl = flagUrl.trim();

  return (
    <article>
      <p style={{marginBottom: 16, color: "#666"}}>
        팀 코드(alpha-2) 입력 시 국기 URL이 자동 채워집니다. 이미지가 깨지면 URL을 직접 수정할 수 있습니다.
      </p>

      <div className="form-container event-form worldcup-form" style={{marginTop: 0}}>
        <div><span>* 팀 코드 (alpha-2)</span></div>
        <div>
          <input
            type="text"
            value={teamCode}
            placeholder="BR"
            maxLength={2}
            style={{width: 80, textTransform: "uppercase"}}
            onChange={(e) => onTeamCodeChange(e.target.value)}
            disabled={!!editing}
          />
          <span style={{marginLeft: 12, color: "#888"}}>예: BR, MX, JP, KR</span>
        </div>

        <div><span>* 팀명</span></div>
        <div>
          <input type="text" value={teamName} placeholder="브라질"
                 onChange={(e) => setTeamName(e.target.value)} style={{width: "100%"}}/>
        </div>

        <div><span>국기 URL</span></div>
        <div>
          <div className="worldcup-flag-url-field">
            <input
              type="text"
              value={flagUrl}
              placeholder="https://flagcdn.com/w80/br.png"
              onChange={(e) => setFlagUrl(e.target.value)}
              style={{width: "100%", maxWidth: 520}}
            />
            <button type="button" className="grey-btn" onClick={applyAutoFlagUrl}>
              코드 기준 자동 생성
            </button>
          </div>
        </div>

        <div><span>국기 미리보기</span></div>
        <div className="worldcup-flag-preview">
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt=""
                width={40}
                height={30}
                style={{objectFit: "cover", border: "1px solid #ddd"}}
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
              />
              <span style={{color: "#666", fontSize: 14}}>
                미리보기 — URL이 잘못되면 이미지가 표시되지 않을 수 있습니다.
              </span>
            </>
          ) : (
            <span style={{color: "#999"}}>팀 코드 입력 또는 URL을 직접 입력해주세요.</span>
          )}
        </div>

        <div><span>정렬</span></div>
        <div>
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}/>
        </div>

        <div><span>사용 여부</span></div>
        <div className="type-input">
          <Form.Check type="radio" id="team-use-y" name="team-use" label="사용"
                      checked={useYn === "Y"} onChange={() => setUseYn("Y")}/>
          <Form.Check type="radio" id="team-use-n" name="team-use" label="미사용"
                      checked={useYn === "N"} onChange={() => setUseYn("N")}/>
        </div>
      </div>

      <div className="flex" style={{gap: 8}}>
        <button type="button" className="blue-btn" onClick={() => setConfirmOpen(true)}>
          {editing ? "수정 저장" : "팀 등록"}
        </button>
        {editing && (
          <button type="button" className="grey-btn" onClick={resetForm}>취소</button>
        )}
      </div>

      <Table bordered className="basic-table" style={{marginTop: 24}}>
        <thead>
        <tr>
          <th>ID</th>
          <th>코드</th>
          <th>국기</th>
          <th>팀명</th>
          <th>정렬</th>
          <th>사용</th>
          <th>관리</th>
        </tr>
        </thead>
        <tbody>
        {teams?.length > 0 ? teams.map((team) => (
          <tr key={team.teamId}>
            <td>{team.teamId}</td>
            <td>{team.teamCode}</td>
            <td>
              {team.flagUrl && (
                <img src={team.flagUrl} alt={team.teamCode} width={32} height={24}
                     style={{objectFit: "cover", border: "1px solid #ddd"}}/>
              )}
            </td>
            <td>{team.teamName}</td>
            <td>{team.sortOrder}</td>
            <td>{team.useYn}</td>
            <td className="button-cell">
              <button type="button" className="grey-btn" onClick={() => startEdit(team)}>수정</button>
            </td>
          </tr>
        )) : (
          <tr>
            <td colSpan={7}>등록된 팀이 없습니다.</td>
          </tr>
        )}
        </tbody>
      </Table>

      <ModalDialog
        show={confirmOpen}
        onHide={() => setConfirmOpen(false)}
        btnText="예"
        secondBtnText="아니요"
        hasTwoButtons
        onOk={onSave}
        onClickSecond={() => setConfirmOpen(false)}
      >
        <p style={{textAlign: "center"}}>
          {editing ? "팀 정보를 수정하시겠습니까?" : "팀을 등록하시겠습니까?"}
        </p>
      </ModalDialog>
    </article>
  );
}
